import { faceCode, faceNotation, type MatchView, type PileEntry, type Side } from '../../engine';
import { t } from '../../i18n';
import { faceLong } from '../../app/describe';
import { CardView, CardBackArt, type CardSize } from './CardView';

export function pileEntryLabel(e: PileEntry): string {
  if (e.source === 'epitaph') return t('pile.epitaphEntry', { face: faceCode(e.face) });
  if (e.source === 'chalk') return t('pile.chalkEntry', { face: faceCode(e.face) });
  return e.wasWild ? t('card.wildNamed', { face: faceNotation(e.face) }) : faceLong(e.face);
}

export function PileCard({ entry, size = 'md' }: { entry: PileEntry; size?: CardSize }) {
  if (entry.source === 'chalk')
    return <CardView size={size} face={entry.face} chalk label={pileEntryLabel(entry)} />;
  return entry.source === 'epitaph' ? (
    <CardView
      size={size}
      face={entry.face}
      epitaph={{ links: [], recordedScore: 0, round: 0 }}
      label={pileEntryLabel(entry)}
    />
  ) : (
    <CardView size={size} face={entry.face} wild={entry.wasWild} label={pileEntryLabel(entry)} />
  );
}

/** How a card lies on the pile: a small, stable tilt, so the pile looks laid by hand. */
export const pileTilt = (index: number): number => ((index * 47) % 11) - 5;

/** The top of the pile in words: what the next card must match. */
export function pileTopText(v: MatchView): string {
  const top = v.pile[v.pile.length - 1];
  if (!top) return t('pile.any');
  return top.source === 'epitaph'
    ? t('pile.epitaphTop', { face: faceCode(top.face) })
    : top.source === 'chalk'
      ? t('pile.chalkTop', { face: faceCode(top.face) })
      : faceNotation(top.face, top.wasWild);
}

/**
 * The shared discard pile (§19.3): the top card face up, the two before it peeking out beneath.
 * Opens the whole pile.
 */
export function PileView({ v, onShowPile }: { v: MatchView; onShowPile: () => void }) {
  const n = v.pile.length;
  const top = v.pile[n - 1];
  const under = v.pile.slice(Math.max(0, n - 3), n - 1);
  const label = top ? t('pile.label', { top: pileEntryLabel(top), n }) : t('match.pileEmpty');
  return (
    <button
      type="button"
      className={`pile ${top ? '' : 'is-empty'}`}
      aria-label={label}
      title={label}
      onClick={onShowPile}
      disabled={n === 0}
      data-pile
    >
      {top ? (
        <>
          {under.map((e, i) => (
            <span
              key={n - under.length - 1 + i}
              className="pile-card pile-under"
              style={{ transform: `rotate(${pileTilt(n - under.length - 1 + i)}deg)` }}
              aria-hidden="true"
            >
              <PileCard entry={e} />
            </span>
          ))}
          <span
            className="pile-card pile-top"
            style={{ transform: `rotate(${pileTilt(n - 1)}deg)` }}
            data-pile-top
          >
            <PileCard entry={top} />
          </span>
          <span className="pile-count" aria-hidden="true">
            {n}
          </span>
        </>
      ) : (
        <span className="pile-slot" aria-hidden="true">
          {t('pile.anyShort')}
        </span>
      )}
    </button>
  );
}

/**
 * A player's face-down deck. On the turn's first decision it is what you tap to draw (the
 * CONTINUE action, §6.1); otherwise it just shows how many cards are left.
 */
export function DeckView({
  side,
  count,
  canDraw,
  onDraw,
}: {
  side: Side;
  count: number;
  canDraw: boolean;
  onDraw: () => void;
}) {
  const layers = Math.min(4, Math.ceil(count / 5));
  const label = canDraw ? t('deck.draw', { n: count }) : t('deck.label', { n: count });
  return (
    <button
      type="button"
      className={`deck ${canDraw ? 'is-live' : ''} ${count === 0 ? 'is-empty' : ''}`}
      aria-label={label}
      title={label}
      disabled={!canDraw}
      onClick={onDraw}
      data-deck={side}
    >
      {count === 0 ? (
        <span className="pile-slot" aria-hidden="true">
          {t('deck.empty')}
        </span>
      ) : (
        <span className="deck-stack" aria-hidden="true">
          {Array.from({ length: layers }, (_, i) => (
            <span
              key={i}
              className="card card-md card-back deck-layer"
              style={{
                transform: `translate(${(layers - 1 - i) * -1.5}px, ${(layers - 1 - i) * 2}px)`,
              }}
            >
              <CardBackArt />
            </span>
          ))}
        </span>
      )}
      {count > 0 && (
        <span className="deck-count" aria-hidden="true">
          {count}
        </span>
      )}
      {canDraw && (
        <span className="deck-cue" aria-hidden="true">
          {t('deck.cue')}
        </span>
      )}
    </button>
  );
}
